import type { WatchQueryFetchPolicy } from '@apollo/client';

import {
    useCompanyGalleriesQuery,
    useCompanyGalleriesWithCollaboratorsQuery,
    useUserGalleriesQuery,
    useUserGalleriesWithCollaboratorsQuery,
} from 'src/hooks/__generated__/queries';
import type { GalleryAccessPolicy, GallerySort } from 'src/types/__generated__/graphql';
import { GallerySortField, SortDirection } from 'src/types/__generated__/graphql';

const defaultSort = { field: GallerySortField.ContentLastUpdatedAt, direction: SortDirection.Desc };

type UseGalleriesWithoutCollaboratorsProps = {
    userId?: string;
    companyId?: string;
    policy: GalleryAccessPolicy;
    limit: number;
    skip?: boolean;
    sort?: GallerySort;
    fetchPolicy?: WatchQueryFetchPolicy;
};

const useGalleriesWithoutCollaborators = ({
    userId,
    companyId,
    policy,
    limit,
    skip,
    sort = defaultSort,
    fetchPolicy,
}: UseGalleriesWithoutCollaboratorsProps) => {
    const {
        data: userGalleries,
        loading: userGalleriesLoading,
        fetchMore: fetchMoreUserGalleries,
    } = useUserGalleriesQuery({
        variables: {
            userId: userId as string,
            pageRequest: {
                offset: 0,
                limit,
            },
            policy,
            sort,
        },
        skip: !userId || skip,
        notifyOnNetworkStatusChange: true,
        fetchPolicy,
    });

    const {
        data: companyGalleries,
        loading: companyGalleriesLoading,
        fetchMore: fetchMoreCompanyGalleries,
    } = useCompanyGalleriesQuery({
        variables: {
            companyId: companyId as string,
            pageRequest: {
                offset: 0,
                limit,
            },
            policy,
            sort,
        },
        skip: !companyId || skip,
        notifyOnNetworkStatusChange: true,
        fetchPolicy,
    });

    const handleFetchMore = () => {
        if (userId) {
            return fetchMoreUserGalleries({
                variables: {
                    pageRequest: {
                        offset: userGalleries?.userProfile?.galleries.items.length,
                        limit,
                    },
                },
            });
        } else {
            return fetchMoreCompanyGalleries({
                variables: {
                    pageRequest: {
                        offset: companyGalleries?.companyProfile.galleries.items.length,
                        limit,
                    },
                },
            });
        }
    };

    return {
        items: userId ? userGalleries?.userProfile?.galleries.items : companyGalleries?.companyProfile.galleries.items,
        totalCount: userId
            ? userGalleries?.userProfile?.galleries.totalCount
            : companyGalleries?.companyProfile.galleries.totalCount,
        loading: userId ? userGalleriesLoading : companyGalleriesLoading,
        fetchMore: handleFetchMore,
    };
};

type UseGalleriesWithCollaboratorsProps = UseGalleriesWithoutCollaboratorsProps & {
    collaboratorsLimit?: number;
};
const useGalleriesWithCollaborators = ({
    userId,
    companyId,
    policy,
    limit,
    collaboratorsLimit = 2,
    skip,
    sort = defaultSort,
    fetchPolicy,
}: UseGalleriesWithCollaboratorsProps) => {
    const {
        data: userGalleries,
        loading: userGalleriesLoading,
        fetchMore: fetchMoreUserGalleries,
    } = useUserGalleriesWithCollaboratorsQuery({
        variables: {
            userId: userId as string,
            pageRequest: {
                offset: 0,
                limit,
            },
            policy,
            sort,
            collaboratorPageRequest: {
                offset: 0,
                limit: collaboratorsLimit,
            },
        },
        skip: !userId || skip,
        notifyOnNetworkStatusChange: true,
        fetchPolicy,
    });

    const {
        data: companyGalleries,
        loading: companyGalleriesLoading,
        fetchMore: fetchMoreCompanyGalleries,
    } = useCompanyGalleriesWithCollaboratorsQuery({
        variables: {
            companyId: companyId as string,
            pageRequest: {
                offset: 0,
                limit,
            },
            policy,
            sort,
            collaboratorPageRequest: {
                offset: 0,
                limit: collaboratorsLimit,
            },
        },
        skip: !companyId || skip,
        notifyOnNetworkStatusChange: true,
        fetchPolicy,
    });

    const handleFetchMore = () => {
        if (userId) {
            return fetchMoreUserGalleries({
                variables: {
                    pageRequest: {
                        offset: userGalleries?.userProfile?.galleries.items.length,
                        limit,
                    },
                },
            });
        } else {
            return fetchMoreCompanyGalleries({
                variables: {
                    pageRequest: {
                        offset: companyGalleries?.companyProfile.galleries.items.length,
                        limit,
                    },
                },
            });
        }
    };

    return {
        items: userId ? userGalleries?.userProfile?.galleries.items : companyGalleries?.companyProfile.galleries.items,
        totalCount: userId
            ? userGalleries?.userProfile?.galleries.totalCount
            : companyGalleries?.companyProfile.galleries.totalCount,
        loading: userId ? userGalleriesLoading : companyGalleriesLoading,
        fetchMore: handleFetchMore,
    };
};

type UseGalleriesProps = UseGalleriesWithCollaboratorsProps & {
    includeCollaborators?: boolean;
};

export const useGalleries = ({ includeCollaborators, collaboratorsLimit, skip, ...rest }: UseGalleriesProps) => {
    const {
        items: itemsWithCollaborators,
        totalCount: totalCountWithCollaborators,
        loading: loadingWithCollaborators,
        fetchMore: fetchMoreWithCollaborators,
    } = useGalleriesWithCollaborators({
        ...rest,
        skip: !includeCollaborators || skip,
        collaboratorsLimit,
    });

    const {
        items: itemsWithoutCollaborators,
        totalCount: totalCountWithoutCollaborators,
        loading: loadingWithoutCollaborators,
        fetchMore: fetchMoreWithoutCollaborators,
    } = useGalleriesWithoutCollaborators({
        ...rest,
        skip: includeCollaborators ?? skip,
    });

    const handleFetchMore = () => {
        if (includeCollaborators) {
            return fetchMoreWithCollaborators();
        } else {
            return fetchMoreWithoutCollaborators();
        }
    };

    return {
        items: includeCollaborators ? itemsWithCollaborators : itemsWithoutCollaborators,
        totalCount: includeCollaborators ? totalCountWithCollaborators : totalCountWithoutCollaborators,
        loading: includeCollaborators ? loadingWithCollaborators : loadingWithoutCollaborators,
        fetchMore: handleFetchMore,
    };
};
